/**
 * E-posta doğrulama — Spec Bölüm 4.5.4
 *
 * 6 haneli kodu kullanıcı email'den okur, buraya girer.
 * Backend: POST /api/auth-extras/verify-email-code
 *
 * Query: ?email=...
 */
import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';

export function EmailVerificationPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const initialEmail = params.get('email') ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (initialEmail) {
      setInfo(`${initialEmail} adresine 6 haneli kod gönderildi.`);
    }
  }, [initialEmail]);

  async function handleVerify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      if (!/^\d{6}$/.test(code)) {
        setError('6 haneli kod girin');
        return;
      }
      await api.post('/api/auth-extras/verify-email-code', { email, code });
      // Doğrulama başarılı → giriş sayfasına yönlendir
      setLocation(
        `/yonetici/giris?verified=1&email=${encodeURIComponent(email)}`,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Beklenmeyen bir hata oluştu');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setError(null);
    setInfo(null);
    setResending(true);
    try {
      await api.post('/api/auth-extras/resend-verification-code', { email });
      setInfo('Yeni kod gönderildi. E-posta kutunuzu kontrol edin.');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Kod gönderilemedi, tekrar deneyin');
      }
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-md px-6 py-12">
        <Card>
          <h1 className="mb-2 text-2xl font-bold text-slate-900">
            E-postanızı Doğrulayın
          </h1>
          <p className="mb-6 text-sm text-slate-600">
            Kayıt sırasında girdiğiniz e-posta adresine gönderdiğimiz 6 haneli
            kodu aşağıya girin. Spam klasörünü kontrol etmeyi unutmayın.
          </p>

          {error && (
            <div className="mb-4">
              <Alert variant="danger">{error}</Alert>
            </div>
          )}
          {info && !error && (
            <div className="mb-4">
              <Alert variant="info">{info}</Alert>
            </div>
          )}

          <form onSubmit={handleVerify} noValidate>
            <Field label="E-posta" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </Field>

            <Field
              label="Doğrulama Kodu"
              htmlFor="code"
              required
              hint="6 haneli sayısal kod (1 saat geçerli)"
            >
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="123456"
                className="text-center text-lg tracking-[0.5em]"
                autoComplete="one-time-code"
              />
            </Field>

            <Button
              type="submit"
              loading={submitting}
              disabled={code.length !== 6 || !email}
              className="w-full"
            >
              Doğrula ve Giriş Yap
            </Button>
          </form>

          <div className="mt-6 border-t border-slate-200 pt-4 text-center text-sm">
            <p className="text-slate-600">Kod gelmedi mi?</p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending || !email}
              className="mt-1 font-medium text-blue-900 hover:underline disabled:text-slate-400"
            >
              {resending ? 'Gönderiliyor…' : 'Yeniden gönder'}
            </button>
          </div>
        </Card>
      </main>
    </div>
  );
}
